import React, { FC, useCallback, useEffect, useState } from "react";
import {
  Grid,
  Snackbar,
  Box,
  Typography,
  Button,
  Checkbox,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Alert from "@mui/material/Alert";
import { useDispatch, useSelector } from "react-redux";
import { IRootReducer } from "../../../reducers";
import {
  DataGrid,
  GridLoadingOverlay,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";

import { getAgenciesRequest } from "../../../actions/agencies";
import { Agency, GetAgency } from "../../../reducers/states/agency";
import CreateAgencyModal from "./create-agency-modal";
import EditAgencyModal from "./edit-agency-modal";
import { AgencyAdminStoreError, AgencyAdminStoreSuccess } from "../../../enums/store-messages/admin";
import { useAgencies } from "../../../hooks/agencies";
import { useJogs } from "../../../hooks/jogs";
import { useAuthContext } from "../../../core/auth-context";
import useUserPermissions from "../../../hooks/user-permissions";
import { RoleAction } from "../../../enums/permission-actions";

export interface AgencyGridRow {
  abbreviation: string;
  id: string;
  isUserAgency: boolean;
  isFilter: boolean;
  name: string;
  shortName: string;
  jogs: string;
  category: string;
}

const AgencyAdminGrid: FC = () => {
  const { authState } = useAuthContext();
  const [disableAgencyAdmin] = useUserPermissions(RoleAction.VIEW_AGENCY_ADMIN);
  
  const dispatch = useDispatch();

  const [rowSelection, setRowSelection] = useState({} as AgencyGridRow);
  const [agencySelection, setAgencySelection] = useState({} as Agency);
  const [selectedRows, setSelectedRows] = useState([] as GridRowSelectionModel);
  // const [invitedEmail, setInvitedEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [gridData, setGridData] = useState([] as AgencyGridRow[]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const store = useSelector((state: IRootReducer) => state.agencyReducer);
  const agencies = useAgencies();
  const jogs = useJogs();

  const showLoadingAnimation = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 1250);

    return function cleanup() {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if(agencies?.length) {
      const rows: AgencyGridRow[] = agencies.map((a) => {
        return {
          abbreviation: a.abbreviation as string,
          id: a.id,
          name: a.name,
          shortName: a.shortName,
          jogs: jogs.getNames(a.jogIds).join(", ") || "",
          category: a.category,
          isFilter: a.isFilter,
          isUserAgency: a.isUserAgency,          
        };
      });
      setGridData(rows);
    } else {
      setIsLoading(true);
      showLoadingAnimation();
    }    
  }, [dispatch, showLoadingAnimation, agencies, jogs]);

  useEffect(() => {
    if(
      store.successMessage === AgencyAdminStoreSuccess.CREATE_AGENCY_SUCCESS
      || store.successMessage === AgencyAdminStoreSuccess.UPDATE_AGENCY_SUCCESS
    ) {
      dispatch(getAgenciesRequest()); // todo: jcavner: I plan to avoid this request by updating state from memory, or a single agency GET....
      setRowSelection({} as AgencyGridRow);
      setSelectedRows([] as GridRowSelectionModel);
      setSuccessMessage(store.successMessage);
    } else if (
      store.failureMessage === AgencyAdminStoreError.CREATE_AGENCY_ERROR
      || store.failureMessage === AgencyAdminStoreError.UPDATE_AGENCY_ERROR
    ) {
      setErrorMessage(store.failureMessage + ". Please cancel or try again.");
    }
  }, [dispatch, store.successMessage, store.failureMessage]);

  const Toolbar: FC = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
      </GridToolbarContainer>
    );
  }

  const handleRowSelection = (newSelectedRows: GridRowSelectionModel) => {
    const lastRowSelected = newSelectedRows.slice(-1);
    setSelectedRows(lastRowSelected);

    const rowId = lastRowSelected[0];
    setRowSelection((rowId && gridData.find(a => a.abbreviation === rowId)) || {} as AgencyGridRow);
    setAgencySelection((rowId && GetAgency(store.agenciesById, rowId.toString())) || {} as Agency);
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1 },   
    { field: "shortName", headerName: "Short Name", flex: 0.75 },    
    { field: "abbreviation", headerName: "Abbreviation", flex: 0.5 },
    { field: "jogs", headerName: "JOG(s)", flex: 1 },
    { field: "category", headerName: "Category", flex: 0.75 },
    { field: "isUserAgency",
      headerName: "Invite Enabled",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => <Checkbox checked={params.row.isUserAgency} readOnly disabled />
    },
    { 
      field: "isFilter",
      headerName: "Has Feed",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => <Checkbox checked={params.row.isFilter} readOnly disabled />
     },
  ];

  const getGridHeight = () => {  
    const headerHeight = 64;
    const appMainBoxMargins = 16;
    const title = 53;
    const total = headerHeight + appMainBoxMargins + title;
    return `calc(100vh - ${total}px`;
  }

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" component="h1">Agency Admin</Typography>
        </Grid>
        <Grid item container xs id="admin-grid" alignItems="center" justifyContent="flex-end">
          {!disableAgencyAdmin ? ([
            <Grid key="edit" item m={1}>
              <EditAgencyModal agencySelection={agencySelection} isEditBtnEnabled={selectedRows?.length === 1} />
            </Grid>,
            <Grid key="create" item m={1}>
              <CreateAgencyModal />
            </Grid>
          ]) : null }          
          <Grid item m={1}>
            <Button
              aria-label="Reload Agencies"
              color="inherit"
              endIcon={<RefreshIcon />}
              onClick={() => dispatch(getAgenciesRequest())}
              style={{ minWidth: "150px" }}
              variant="contained"
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Grid>
        <Box style={{ height: getGridHeight() }}>
          <DataGrid
            checkboxSelection
            columns={columns}
            disableColumnSelector
            onPaginationModelChange={setPaginationModel}
            onRowSelectionModelChange={(s) => handleRowSelection(s)}
            paginationModel={paginationModel}
            rows={gridData}
            rowSelectionModel={selectedRows}
            slots={{
              loadingOverlay: GridLoadingOverlay,
              toolbar: Toolbar,
            }}
            loading={isLoading}
          />
        </Box>
      
      <Snackbar
        open={errorMessage !== ""}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setErrorMessage("")}
      >
        <Alert onClose={() => setErrorMessage("")} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={successMessage !== ""}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSuccessMessage("")}
      >
        <Alert onClose={() => setSuccessMessage("")} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AgencyAdminGrid;
